import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../constants';
import { fetchWrapper } from '../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IPayor, IPayorProviderCredential } from '../utils/types';

const name = 'payor_provider_credentials';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<PayorProviderCredentialsState>) =>
    createExtraReducers(builder),
});

export interface PayorProviderCredentialsState {
  list: Array<IPayorProviderCredential>;
  error: any;
  loading: boolean;
  // Latest getList request — drop stale/aborted results so they cannot clear loading for a newer fetch.
  requestId: string | null;
}

function createInitialState(): PayorProviderCredentialsState {
  return {
    list: [],
    loading: false,
    error: null,
    requestId: null,
  };
}

function createReducers() {
  return {};
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/payor_provider_credentials`;

  return {
    getList: getList(),
    createPayorProviderCredential: createPayorProviderCredential(),
    updatePayorProviderCredential: updatePayorProviderCredential(),
    deletePayorProviderCredential: deletePayorProviderCredential(),
  };

  function getList() {
    return createAsyncThunk(`${name}/getList`, async ({ params, getAccessTokenSilently }: any) => {
      let baseSearch = baseUrl;
      if (params) {
        const baseSearchParams = Object.entries(params)
          .map(([key, _value]) => `${key}=${encodeURIComponent(params[key])}`)
          .join('&');
        baseSearch += `/?${baseSearchParams}`; // Append query parameters
      } else {
        baseSearch += '/'; // Append trailing slash if no params provided
      }
      const response = await fetchWrapper.get({
        url: baseSearch,
        getAccessTokenSilently,
      });
      return response;
    });
  }

  function createPayorProviderCredential() {
    return createAsyncThunk(
      `${name}/createPayorProviderCredential`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }
  function updatePayorProviderCredential() {
    return createAsyncThunk(
      `${name}/updatePayorProviderCredential`,
      async ({ data, getAccessTokenSilently, payorProviderCredId }: any) => {
        const response = await fetchWrapper.patch(
          { url: `${baseUrl}/${payorProviderCredId}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function deletePayorProviderCredential() {
    return createAsyncThunk(
      `${name}/deletePayorProviderCredential`,
      async ({ payorProviderCredId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${payorProviderCredId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<PayorProviderCredentialsState>) {
  const {
    getList,
    createPayorProviderCredential,
    updatePayorProviderCredential,
    deletePayorProviderCredential,
  } = extraActions;

  builder
    // getList()
    .addCase(
      getList.pending,
      (state: PayorProviderCredentialsState, action: { meta: { requestId: string } }) => {
        state.requestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      },
    )
    .addCase(
      getList.fulfilled,
      (
        state: PayorProviderCredentialsState,
        action: PayloadAction<IPayorProviderCredential[], string, { requestId: string }>,
      ) => {
        if (action.meta.requestId !== state.requestId) return;
        state.list = action.payload;
        state.loading = false;
      },
    )
    .addCase(getList.rejected, (state: PayorProviderCredentialsState, action: any) => {
      if (action.meta.requestId !== state.requestId) return;
      state.loading = false;
      if (action.meta.aborted) return;
      state.list = [];
      state.error = action.error;
    })

    // createPayorProviderCredential()
    .addCase(createPayorProviderCredential.pending, (state: PayorProviderCredentialsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createPayorProviderCredential.fulfilled,
      (state: PayorProviderCredentialsState, _action: PayloadAction<IPayorProviderCredential>) => {
        state.loading = false;
        toast.success('New payor provider credential created successfully');
      },
    )
    .addCase(
      createPayorProviderCredential.rejected,
      (state: PayorProviderCredentialsState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(state.error.message);
      },
    )

    //updatePayorProviderCredential()
    .addCase(updatePayorProviderCredential.pending, (state: PayorProviderCredentialsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      updatePayorProviderCredential.fulfilled,
      (state: PayorProviderCredentialsState, _action: PayloadAction<IPayor>) => {
        state.loading = false;
        toast.success('Payor provider credential updated successfully');
      },
    )
    .addCase(
      updatePayorProviderCredential.rejected,
      (state: PayorProviderCredentialsState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(state.error.message);
      },
    )

    //deletePayorProviderCredential()
    .addCase(deletePayorProviderCredential.pending, (state: PayorProviderCredentialsState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      deletePayorProviderCredential.fulfilled,
      (state: PayorProviderCredentialsState, _action: PayloadAction<any>) => {
        state.loading = false;
        toast.success('Payor provider credential deleted successfully');
      },
    )
    .addCase(
      deletePayorProviderCredential.rejected,
      (state: PayorProviderCredentialsState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(state.error.message);
      },
    );
}

export const payorProviderCredentialsActions = { ...slice.actions, ...extraActions };
export const payorProviderCredentialsReducer = slice.reducer;
